//framework
import React, { Component } from 'react';
import { Route } from 'react-router';
import * as Colyseus from "@colyseus/sdk";
import nosleep from 'nosleep.js';

//components
import Loading from 'components/Shared/Loading/Loading';
import ClientMenuIcon from 'components/Client/SubComponents/MenuIcons/ClientMenuIcon';
import DebugPanel from 'components/Shared/DebugPanel/DebugPanel';
import QuestionView from "components/Client/Views/QuestionView/QuestionView";
import WaitingView from 'components/Client/Views/WaitingView/WaitingView';
import CategorySelectView from 'components/Client/Views/CategorySelectView/CategorySelectView';
import StartGameView from 'components/Client/Views/StartGameView/StartGameView';
import GameOverView from 'components/Client/Views/GameOverView/GameOverView';
import VideoPlayingView from 'components/Client/Views/VideoPlayingView/VideoPlayingView';

//styles
import styles from './Client.module.scss';

//constants
import { GAME_STATES, GAME_ID, MESSAGES } from 'constants/constants'
import { TEST_ROOM_STATE } from 'constants/testRoomState';
import { isTerminalByCode, retryDelayForCode, isTerminalReconnectError } from 'services/connectionManager';

var noSleep = new nosleep();

//dev settings
const useTestState = false && process.env.REACT_APP_ENVIRONMENT === "development";
const preventRedirect = false && process.env.REACT_APP_ENVIRONMENT === "development";

export class Client extends Component {
    static displayName = Client.name;

    constructor(props) {
        super(props);

        this.client = new Colyseus.Client(process.env.REACT_APP_GAME_SERVER_URL);
        this.state = {
            roomId: 0,
            room: null,
            myId: useTestState ? "1" : null,
            roomState: useTestState ? TEST_ROOM_STATE : null,
            redirect: null,
            redirectURL: "",
            gameState: GAME_STATES.LOADING,
            connected: false,
            showReconnectPopup: false,
            reconnectionToken: null,
        };
        this.reconnectTimer = null;
    }

    componentDidMount() {
        window.addEventListener('offline', this.handleOffline);
        window.addEventListener('online', this.handleOnline);

        // Call doReconnect immediately like Host does
        this.doReconnect();

        document.addEventListener('click', function enableNoSleep() {
            document.removeEventListener('click', enableNoSleep, false);
            noSleep.enable();
        }, false);
    }

    componentWillUnmount() {
        window.removeEventListener('offline', this.handleOffline);
        window.removeEventListener('online', this.handleOnline);
        if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    }

    handleOffline = () => {
        if (this.state.room) {
            try {
                this.state.room.reconnection.maxRetries = 0;
                this.state.room.removeAllListeners();
                this.state.room.leave(false);
            } catch { }
        }
        this.setState({ connected: false, showReconnectPopup: true, room: null });
        this.scheduleReconnect(1000);
    };

    handleOnline = () => {
        this.setState({ connected: false, room: null }, () => {
            this.scheduleReconnect(0);
        });
    };

    getReconnectionToken = () => {
        const params = new URLSearchParams(window.location.search);
        const fromUrl = params.get('token');
        if (fromUrl) return fromUrl;
        if (this.state.reconnectionToken) return this.state.reconnectionToken;
        return null;
    };

    goToLobby() {
        const token = this.getReconnectionToken();
        this.setState({
            redirect: true,
            redirectURL: `${this.getRedirectURL()}/?token=${token}`,
        });
        this.state.room.reconnection.maxRetries = 0;
        try { this.state.room?.leave(false); } catch { }
        console.log("going to lobby", this.state.redirectURL);
    }

    getRedirectURL() {
        let url = process.env.REACT_APP_GAME_CITY_URL;
        if (this.state.room) {
            if (this.state.room.name !== "game_city_room") {
                url = process.env.REACT_APP_GAME_CITY_URL;
            }
        }
        return url;
    }

    updateToken(token) {
        try {
            const url = new URL(window.location.href);
            url.searchParams.set('token', token);
            window.history.replaceState(null, '', url.toString());
        } catch (e) {
            console.warn(e);
        }
    }

    scheduleReconnect = (delayMs = 2000) => {
        if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
        this.reconnectTimer = setTimeout(() => this.doReconnect(), delayMs);
    };

    getRenderView(gameState, players, roomState, room) {
        console.log("this.state.gameState:", this.state.gameState);

        const player = players?.find(p => p.id === this.state.myId);
        const categoryChoosingId = roomState?.mdmdData?.playerChoosingCategory;

        if (player?.mdmdData?.waitingToJoin) {
            return <WaitingView player={player} room={room} text="Game in progress please wait for the current game to end..." />;
        }

        if (player?.mdmdData?.totalMoney === 0 && gameState !== GAME_STATES.GAME_OVER && gameState !== GAME_STATES.INTRO_VIDEO) {
            return <WaitingView player={player} room={room} text="You're out!" />;
        }

        switch (gameState) {
            case GAME_STATES.LOADING:
                return <WaitingView player={player} room={room} text="Loading..." />;

            case GAME_STATES.START_MENU:
                return <StartGameView player={player} room={room} />;

            case GAME_STATES.CATEGORY_SELECT:
                if (categoryChoosingId === this.state.myId && !roomState.mdmdData?.categorySelected) {
                    const categories = roomState?.mdmdData?.categories || [];
                    return <CategorySelectView categories={categories} player={player} room={room} />;
                }

                if (!roomState.mdmdData?.categorySelected) {
                    const playerChoosingCategory = players?.find(p => p.id === categoryChoosingId);
                    return <WaitingView player={player} room={room} text={`${playerChoosingCategory?.name} is choosing a category`} />;
                }

                return <WaitingView player={player} room={room} text={`Category selected: ${roomState.mdmdData?.categorySelected}`} />;

            case GAME_STATES.ANSWERING:
                const answers = roomState?.mdmdData?.answers || [];
                return <QuestionView answers={answers} playerMdmdData={player?.mdmdData} room={room} />;

            case GAME_STATES.GAME_OVER:
                return <GameOverView room={room} />;


            case GAME_STATES.GAME_START_VIDEO:
            case GAME_STATES.INTRO_VIDEO:
                return <VideoPlayingView player={player} room={room} />
            default:
                return <WaitingView player={player} room={room} />;
        }
    }

    doReconnect = () => {
        const token = this.getReconnectionToken();
        const offline = typeof navigator !== "undefined" && navigator.onLine === false;

        if (!token) {
            this.setState({ connected: false, showReconnectPopup: true });
            if (!offline) {
                this.setState({ redirect: true, redirectURL: `${this.getRedirectURL()}/` });
            } else {
                this.scheduleReconnect(3000);
            }
            return;
        }

        if (this.state.connected) return;

        this.client.reconnect(token).then(room => {
            if (this.reconnectTimer) clearTimeout(this.reconnectTimer);

            console.log(room.sessionId, "joined", room.name);
            this.setState({
                room: room,
                connected: true,
                showReconnectPopup: false,
                roomId: room.roomId,
                myId: room.sessionId,
                reconnectionToken: room.reconnectionToken
            });
            this.updateToken(room.reconnectionToken);
            room?.send("update_player_token", { reconnectionToken: room.reconnectionToken });

            room.onStateChange.once((roomState) => {
                console.log("this is the first room state!", roomState);
                const player = roomState.players.get(room.sessionId);
                if (!player) this.setState({ redirect: true, redirectURL: `${this.getRedirectURL()}/` });

                const serverGameId = roomState?.mdmdData?.gameId || roomState?.gameId || room?.name;
                if (serverGameId && serverGameId !== GAME_ID) {
                    this.goToLobby();
                    return;
                }

                this.setState({ roomState: roomState, player, gameState: roomState.mdmdData.gameState });
            });

            room.onStateChange((state) => {
                if (state.gameId !== GAME_ID) {
                    this.goToLobby();
                    return;
                }
                this.setState({ roomState: state, gameState: state.mdmdData.gameState });
            });

            room.onMessage("game_starting", (message) => {
                console.log("game_starting", "received on", room.name, message);
                if (message.gameId !== GAME_ID) {
                    this.goToLobby();
                }
            });

            room.onMessage("host_joined_lobby", (message) => {
                console.log("host_joined_lobby", "received on", room.name, message);
                this.goToLobby();
            });

            room.onMessage(MESSAGES.TO_CLIENTS.CHANGE_GAME, (message) => {
                console.log("change_game", "received on", room.name, message);
                this.goToLobby();
            });

            room.onError((code, message) => {
                console.log(this.client.id, "room error", code, message);
            });

            room.onLeave((code) => {
                if (this.state.redirectURL) return;
                console.log(this.client.id, "left", room.name, code);

                const nowOffline = typeof navigator !== "undefined" && navigator.onLine === false;

                if (!nowOffline && isTerminalByCode(code)) {
                    this.setState({ connected: false, showReconnectPopup: true });
                    this.goToLobby();
                } else {
                    const delay = retryDelayForCode(code);
                    this.setState({ connected: false, showReconnectPopup: true });
                    this.scheduleReconnect(delay);
                }
            });

        }).catch(e => {
            console.log("JOIN/RECONNECT ERROR", e);
            const nowOffline = typeof navigator !== "undefined" && navigator.onLine === false;

            if (!nowOffline && isTerminalReconnectError(e)) {
                this.setState({ connected: false, redirect: true, redirectURL: `${this.getRedirectURL()}/` });
                return;
            }
            this.setState({ connected: false, showReconnectPopup: true });
            this.scheduleReconnect(2000);
        });
    }

    render() {
        if (this.state.redirectURL && !preventRedirect) {
            return (
                <React.Fragment>
                    <div id="clientContainer" className={styles.clientContainer}>
                        <Loading loadingText={"Sending you to the lobby!"} />
                    </div>
                    <div style={{ opacity: 0 }}>
                        {this.state.redirect ? <Route path="/" render={() => (window.location = this.state.redirectURL)} /> : null}
                    </div>
                </React.Fragment>
            )
        }

        const { room, roomState, connected, gameState, showReconnectPopup } = this.state;
        const players = Array.from(roomState?.players?.values() ?? []);
        const isLoading = gameState === GAME_STATES.LOADING;
        return (
            <div>
                <div id="clientContainer" className={`${styles.clientContainer}`} style={{ margin: 0 }}>
                    {(!connected || isLoading) && (
                        <Loading loadingText={showReconnectPopup ? "Disconnected, Reconnecting" : isLoading ? "Loading" : "Connecting"}
                            noBg={true} hideLoader={false}
                        />
                    )}

                    {(connected && !isLoading) && (
                        <>
                            <ClientMenuIcon room={room} gameState={gameState} mutedMusic={roomState?.mdmdData?.mutedMusic} mutedSfx={roomState?.mdmdData?.mutedSfx} />
                            {this.getRenderView(gameState, players, roomState, room)}
                        </>
                    )}
                </div>

                {useTestState && (
                    <DebugPanel players={players} state={this.state} onSetState={(updates) => this.setState(updates)} />
                )}
            </div>
        )
    }
}